import React, { useContext } from 'react';
import { ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import { format } from 'date-fns';
import * as XLSX from 'xlsx';
import GoogleSheetsIcon from '../../../assets/images/google_sheets.svg?react';
import AppContext from '../../../context/AppContext';

const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    return format(date, 'dd.MM.yyyy');
  } catch (e) {
    return '';
  }
};

// Функция для создания Excel файла с красивым форматированием
const exportToExcel = (data) => {
  const workbook = XLSX.utils.book_new();

  // 1. ЛИСТ "SUMMARY" - Личная информация и общая информация
  const summaryData = [];

  // Заголовок
  summaryData.push(['🎯 CV BUILDER RESUME EXPORT']);
  summaryData.push(['📅 Generated on:', formatDate(new Date())]);
  summaryData.push([
    '👤 Candidate:',
    `${data.summary?.firstName || ''} ${data.summary?.lastName || ''}`,
  ]);
  summaryData.push(['']);

  // Личная информация
  summaryData.push(['👤 PERSONAL INFORMATION']);
  summaryData.push(['Field', 'Value']);
  if (data.summary?.enable !== false) {
    summaryData.push(['First Name', data.summary?.firstName || '']);
    summaryData.push(['Last Name', data.summary?.lastName || '']);
    summaryData.push(['Position', data.summary?.position || '']);
    summaryData.push(['Position Level', data.summary?.position_level || '']);
    summaryData.push(['Experience (years)', data.summary?.experience || '']);
    summaryData.push(['Summary', data.summary?.body || '']);
  }
  summaryData.push(['']);

  // Информация о CV
  summaryData.push(['📄 CV INFORMATION']);
  summaryData.push(['Field', 'Value']);
  summaryData.push(['CV Name', data.name || '']);
  summaryData.push(['CV Status', data.cvStatus || '']);
  summaryData.push(['Is Main CV', data.isMain ? '✅ Yes' : '❌ No']);
  summaryData.push(['Created At', formatDate(data.createdAt)]);
  summaryData.push(['Updated At', formatDate(data.updatedAt)]);
  summaryData.push(['Updated By', data.updatedBy || '']);
  summaryData.push(['']);

  // Языки
  if (data.languages?.enable !== false && data.languages?.items?.length > 0) {
    summaryData.push(['🌍 LANGUAGES']);
    summaryData.push(['Language', 'Level']);
    data.languages.items
      .filter((item) => item.enable !== false)
      .forEach((item) => {
        summaryData.push([item.key, item.level || '']);
      });
    summaryData.push(['']);
  }

  // Образование
  if (data.education?.enable !== false && data.education?.items?.length > 0) {
    summaryData.push(['🎓 EDUCATION']);
    summaryData.push(['Institution', 'Major', 'Grade', 'Description']);
    data.education.items
      .filter((item) => item.enable !== false)
      .forEach((item) => {
        summaryData.push([item.key, item.major || '', item.grade || '', item.description || '']);
      });
    summaryData.push(['']);
  }

  // Сертификаты
  if (data.certifications?.enable !== false && data.certifications?.items?.length > 0) {
    summaryData.push(['🏆 CERTIFICATIONS']);
    summaryData.push(['Name', 'Issued By', 'Start Date', 'End Date', 'Link', 'State Accredited']);
    data.certifications.items
      .filter((item) => item.enable !== false)
      .forEach((item) => {
        const tags = item.tags?.map((t) => t.value || t.key).join('; ') || '';
        summaryData.push([
          item.key,
          item.issued || '',
          formatDate(item.start),
          formatDate(item.end),
          item.link || '',
          item.isStateAccredited ? 'Yes' : 'No',
          tags ? `Tags: ${tags}` : '',
        ]);
      });
  }

  const summarySheet = XLSX.utils.aoa_to_sheet(summaryData);

  // Настройка ширины колонок для Summary
  summarySheet['!cols'] = [
    { wch: 25 }, // Колонка A - Field/Name
    { wch: 50 }, // Колонка B - Value
    { wch: 20 }, // Колонка C - дополнительные поля
    { wch: 15 }, // Колонка D
    { wch: 15 }, // Колонка E
    { wch: 30 }, // Колонка F
    { wch: 40 }, // Колонка G - Tags
  ];

  XLSX.utils.book_append_sheet(workbook, summarySheet, 'Summary');

  // 2. ЛИСТ "SKILLS & TECHNOLOGIES" - Все навыки и технологии
  const skillsData = [];

  // Домены
  if (data.domains?.enable !== false && data.domains?.items?.length > 0) {
    skillsData.push(['🏢 DOMAINS']);
    skillsData.push(['Domain']);
    data.domains.items.forEach((item) => {
      skillsData.push([item.name || item.title || '']);
    });
    skillsData.push(['']);
  }

  // Ключевые навыки
  if (data.keySkills?.enable !== false && data.keySkills?.items?.length > 0) {
    skillsData.push(['🔑 KEY SKILLS']);
    skillsData.push(['Skill']);
    data.keySkills.items.forEach((item) => {
      skillsData.push([item.title || '']);
    });
    skillsData.push(['']);
  }

  // Soft Skills
  if (data.skills?.enable !== false && data.skills?.items?.length > 0) {
    skillsData.push(['💡 SOFT SKILLS']);
    skillsData.push(['Skill']);
    data.skills.items
      .filter((item) => item.enable !== false)
      .forEach((item) => {
        skillsData.push([item.value || '']);
      });
    skillsData.push(['']);
  }

  // Языки программирования
  if (data.programmingLanguages?.enable !== false && data.programmingLanguages?.items?.length > 0) {
    skillsData.push(['💻 PROGRAMMING LANGUAGES']);
    skillsData.push(['Language', 'Years of Experience', 'Level']);
    data.programmingLanguages.items
      .filter((item) => item.enable !== false)
      .forEach((item) => {
        skillsData.push([item.key, item.years || '', item.level || '']);
      });
    skillsData.push(['']);
  }

  // Технологии разработки
  if (
    data.developmentTechnologies?.enable !== false &&
    data.developmentTechnologies?.items?.length > 0
  ) {
    skillsData.push(['⚙️ DEVELOPMENT TECHNOLOGIES']);
    skillsData.push(['Technology', 'Years of Experience', 'Level']);
    data.developmentTechnologies.items
      .filter((item) => item.enable !== false)
      .forEach((item) => {
        skillsData.push([item.key, item.years || '', item.level || '']);
      });
    skillsData.push(['']);
  }

  // IDE/Tools
  if (data.ideOrTools?.enable !== false && data.ideOrTools?.items?.length > 0) {
    skillsData.push(['🛠️ IDE / DEVELOPMENT TOOLS']);
    skillsData.push(['Tool', 'Years of Experience', 'Level']);
    data.ideOrTools.items
      .filter((item) => item.enable !== false)
      .forEach((item) => {
        skillsData.push([item.key, item.years || '', item.level || '']);
      });
    skillsData.push(['']);
  }

  // Базы данных
  if (data.dataBases?.enable !== false && data.dataBases?.items?.length > 0) {
    skillsData.push(['🗄️ DATABASES']);
    skillsData.push(['Database', 'Years of Experience', 'Level']);
    data.dataBases.items
      .filter((item) => item.enable !== false)
      .forEach((item) => {
        skillsData.push([item.key, item.years || '', item.level || '']);
      });
    skillsData.push(['']);
  }

  // Системы контроля версий
  if (
    data.versionControlSystems?.enable !== false &&
    data.versionControlSystems?.items?.length > 0
  ) {
    skillsData.push(['📋 VERSION CONTROL SYSTEMS']);
    skillsData.push(['System', 'Years of Experience', 'Level']);
    data.versionControlSystems.items
      .filter((item) => item.enable !== false)
      .forEach((item) => {
        skillsData.push([item.key, item.years || '', item.level || '']);
      });
    skillsData.push(['']);
  }

  // CI/CD Tools
  if (data.ciCdTools?.enable !== false && data.ciCdTools?.items?.length > 0) {
    skillsData.push(['🔄 CI/CD TOOLS']);
    skillsData.push(['Tool', 'Years of Experience', 'Level']);
    data.ciCdTools.items
      .filter((item) => item.enable !== false)
      .forEach((item) => {
        skillsData.push([item.key, item.years || '', item.level || '']);
      });
    skillsData.push(['']);
  }

  // iOS Technologies
  if (data.iOsTechnologies?.enable !== false && data.iOsTechnologies?.items?.length > 0) {
    skillsData.push(['📱 iOS TECHNOLOGIES']);
    skillsData.push(['Technology', 'Years of Experience', 'Level']);
    data.iOsTechnologies.items
      .filter((item) => item.enable !== false)
      .forEach((item) => {
        skillsData.push([item.key, item.years || '', item.level || '']);
      });
    skillsData.push(['']);
  }

  // Android Technologies
  if (data.androidTechnologies?.enable !== false && data.androidTechnologies?.items?.length > 0) {
    skillsData.push(['🤖 ANDROID TECHNOLOGIES']);
    skillsData.push(['Technology', 'Years of Experience', 'Level']);
    data.androidTechnologies.items
      .filter((item) => item.enable !== false)
      .forEach((item) => {
        skillsData.push([item.key, item.years || '', item.level || '']);
      });
    skillsData.push(['']);
  }

  // Development Methodologies
  if (
    data.developmentMethodologies?.enable !== false &&
    data.developmentMethodologies?.items?.length > 0
  ) {
    skillsData.push(['📊 DEVELOPMENT METHODOLOGIES']);
    skillsData.push(['Methodology', 'Years of Experience', 'Level']);
    data.developmentMethodologies.items
      .filter((item) => item.enable !== false)
      .forEach((item) => {
        skillsData.push([item.key, item.years || '', item.level || '']);
      });
    skillsData.push(['']);
  }

  // Other Skills
  if (data.other?.enable !== false && data.other?.items?.length > 0) {
    skillsData.push(['🔧 OTHER SKILLS']);
    skillsData.push(['Skill', 'Years of Experience', 'Level']);
    data.other.items
      .filter((item) => item.enable !== false)
      .forEach((item) => {
        skillsData.push([item.key, item.years || '', item.level || '']);
      });
  }

  const skillsSheet = XLSX.utils.aoa_to_sheet(skillsData);

  // Настройка ширины колонок для Skills
  skillsSheet['!cols'] = [
    { wch: 30 }, // Колонка A - Technology/Skill
    { wch: 15 }, // Колонка B - Years
    { wch: 10 }, // Колонка C - Level
  ];

  XLSX.utils.book_append_sheet(workbook, skillsSheet, 'Skills & Technologies');

  // 3. ЛИСТ "PROJECTS & EXPERIENCE" - Проекты и опыт работы
  const projectsData = [];

  if (data.projects?.enable !== false && data.projects?.items?.length > 0) {
    projectsData.push(['🚀 PROJECTS & EXPERIENCE']);
    projectsData.push(['']);
    projectsData.push([
      'Project Name',
      'Alternative Name',
      'Description',
      'Role',
      'Type',
      'Start Date',
      'End Date',
      'Tasks Executed',
      'Technologies',
    ]);

    data.projects.items
      .filter((item) => item.enable !== false)
      .forEach((item) => {
        const startDate = item.start ? formatDate(item.start) : 'Present';
        const endDate = item.end ? formatDate(item.end) : 'Present';
        projectsData.push([
          item.key,
          item.alternativeName || '',
          item.body || '',
          item.role || '',
          item.type || '',
          startDate,
          endDate,
          item.tasksExecuted || '',
          item.task_body || '',
        ]);
      });
  }

  const projectsSheet = XLSX.utils.aoa_to_sheet(projectsData);

  projectsSheet['!cols'] = [
    { wch: 25 },
    { wch: 25 },
    { wch: 50 },
    { wch: 20 },
    { wch: 20 },
    { wch: 15 },
    { wch: 15 },
    { wch: 60 },
    { wch: 40 },
  ];

  XLSX.utils.book_append_sheet(workbook, projectsSheet, 'Projects & Experience');

  const fileName = `CV ${data.summary?.firstName || ''} ${
    data.summary?.lastName || ''
  } (${formatDate(new Date())}).xlsx`;
  XLSX.writeFile(workbook, fileName);
};

const CsvItems = ({ handleClose }) => {
  const context = useContext(AppContext);
  const { state } = context;
  const { data } = state;

  const handleExportExcel = () => {
    exportToExcel(data);
    handleClose();
  };

  return (
    <ListItem button style={{ gap: '8px', padding: '4px 10px' }} onClick={handleExportExcel}>
      <ListItemIcon style={{ minWidth: 0, width: '20px', height: '20px' }}>
        <GoogleSheetsIcon style={{ width: '20px', height: '20px' }} />
      </ListItemIcon>
      <ListItemText primary="Export as XLSX" />
    </ListItem>
  );
};

export default CsvItems;
